import React, { useState, useMemo, useEffect, useImperativeHandle, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import CheckBox from "app/components/inputs/CheckBox";
import SelectBox from "app/components/OptionButtonGroup/SelectBox";
import { generateFromHourOptions, generateToHourOptions, DEFAULT_SCHEDULING_INTERVAL } from "../utils";
import CircleInfo from "../../../../../public/images/circle-info.svg";
import styles from "./styles.module.scss";

type HourOption = { value: string; label: string };

type PropsDef = {
  isAutomaticTracking: boolean;
  schedulingInterval?: number;
  offeringType: "package" | "subscription";
};

export type RestrictUsageRef = {
  getValues: () => {
    restrictWeekdays: boolean;
    restrictWeekends: boolean;
    weekdayBeginAt: number;
    weekdayEndAt: number;
  };
  validate: () => string | null;
};

export default forwardRef<RestrictUsageRef, PropsDef>(function RestrictUsage(props, ref) {
  const { isAutomaticTracking, schedulingInterval, offeringType } = props;
  const { t } = useTranslation();

  const interval = schedulingInterval || DEFAULT_SCHEDULING_INTERVAL;
  const fromHourOptions = useMemo(() => generateFromHourOptions(interval), [interval]);
  const toHourOptions = useMemo(() => generateToHourOptions(interval), [interval]);
  const defaultFromOption = fromHourOptions[0];
  const defaultToOption = toHourOptions[toHourOptions.length - 1];

  const [restrictWeekdays, setRestrictWeekdays] = useState(false);
  const [restrictWeekends, setRestrictWeekends] = useState(false);
  const [weekdayFromHour, setWeekdayFromHour] = useState<HourOption>(defaultFromOption);
  const [weekdayToHour, setWeekdayToHour] = useState<HourOption>(defaultToOption);
  const [hourValidationError, setHourValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAutomaticTracking) {
      setRestrictWeekdays(false);
      setRestrictWeekends(false);
      setWeekdayFromHour(defaultFromOption);
      setWeekdayToHour(defaultToOption);
      setHourValidationError(null);
    }
  }, [isAutomaticTracking]);

  useImperativeHandle(ref, () => ({
    getValues: () => ({
      restrictWeekdays,
      restrictWeekends,
      weekdayBeginAt: Number(weekdayFromHour.value),
      weekdayEndAt: Number(weekdayToHour.value),
    }),
    validate: () => {
      if (restrictWeekdays) {
        const from = Number(weekdayFromHour.value);
        const to = Number(weekdayToHour.value);
        if (from >= to) {
          const error = t("End time must be after start time");
          setHourValidationError(error);
          return error;
        }
      }
      setHourValidationError(null);
      return null;
    },
  }));

  return (
    <div className={styles.restrict_usage_section}>
      {!isAutomaticTracking && (
        <p className={styles.restrict_usage_note}>
          <img src={CircleInfo} alt="Info" className={styles.restrict_usage_note_icon} />
          {t("Time restrictions are only available for offerings with automatic tracking")}
        </p>
      )}
      <p className={styles.restrict_usage_title}>
        {t("Restrict usage to")}
      </p>
      <p className={styles.restrict_usage_subtitle}>
        {offeringType === "package"
          ? t("Limit when this package can be used for booking lessons")
          : t("Limit when this subscription can be used for booking lessons")}
      </p>
      <div className={`${styles.restrict_usage_options} ${!isAutomaticTracking ? styles.restrict_usage_disabled : ''}`}>
        <CheckBox
          name="restrictWeekdays"
          checked={restrictWeekdays}
          onChange={(v: boolean) => {
            setRestrictWeekdays(v);
            if (!v) {
              setWeekdayFromHour(defaultFromOption);
              setWeekdayToHour(defaultToOption);
              setHourValidationError(null);
            }
          }}
          squareCheckIcons
          disabled={!isAutomaticTracking}
          label={
            <span className={styles.restrict_usage_label}>
              {t("Weekdays")}
              <span className={styles.restrict_usage_hint}>
                ({t("Monday - Friday")})
              </span>
            </span>
          }
        />
        {restrictWeekdays && (
          <div className={styles.hour_dropdowns_row}>
            <div className={styles.hour_dropdown_item}>
              <span className={styles.hour_dropdown_label}>{t("From")}</span>
              <SelectBox
                options={fromHourOptions}
                value={weekdayFromHour}
                setValue={(val) => {
                  setWeekdayFromHour(val);
                  setHourValidationError(null);
                }}
                customWidth="100%"
                customControl={{ borderRadius: "8px" }}
              />
            </div>
            <div className={styles.hour_dropdown_item}>
              <span className={styles.hour_dropdown_label}>{t("To")}</span>
              <SelectBox
                options={toHourOptions}
                value={weekdayToHour}
                setValue={(val) => {
                  setWeekdayToHour(val);
                  setHourValidationError(null);
                }}
                customWidth="100%"
                customControl={{ borderRadius: "8px" }}
              />
            </div>
          </div>
        )}
        {hourValidationError && (
          <p className={styles.error_text}>{hourValidationError}</p>
        )}
        <CheckBox
          name="restrictWeekends"
          checked={restrictWeekends}
          onChange={(v: boolean) => setRestrictWeekends(v)}
          squareCheckIcons
          disabled={!isAutomaticTracking}
          label={
            <span className={styles.restrict_usage_label}>
              {t("Weekends & Holidays")}
              <span className={styles.restrict_usage_hint}>
                ({t("Saturday - Sunday")})
              </span>
            </span>
          }
        />
      </div>
    </div>
  );
});
